import {createContext, forwardRef, useContext, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {createPortal} from 'react-dom';
import {CSSTransition} from 'react-transition-group';
import {Typography} from '../atoms/Typography';
import {Button} from '../atoms/Button';
import {CloseLineIcon} from '../svg/CloseLineIcon';
import {formatHeaderTitle} from '../utils/utils';
import {Divider} from '../atoms/Divider';

export const BottomSheet = (props) => {
  const {title, onClose, 'data-qa-id': dataQaId = 'bottom-sheet'} = props;
  const fadeRef = useRef(null);
  const slideRef = useRef(null);

  const formattedTitle = useMemo(() => formatHeaderTitle(title), [title]);

  const params = {...props, title: formattedTitle, 'data-qa-id': dataQaId};

  return (
    <BottomSheetPortal {...params}>
      <Fade ref={fadeRef}>
        <div ref={fadeRef}>
          <Button
            variant="unstyled-button"
            className="bottom-sheet__overlay"
            onClick={onClose}
            aria-label="Close"
            data-qa-id={`${dataQaId}__overlay`}
          />
          <SlideUp ref={slideRef}>
            <Sheet ref={slideRef} {...params} />
          </SlideUp>
        </div>
      </Fade>
    </BottomSheetPortal>
  );
};

BottomSheet.propTypes = {
  in: PropTypes.bool,
  onClose: PropTypes.func,
  content: PropTypes.node,
  'data-qa-id': PropTypes.string,
  tall: PropTypes.bool,
};

const BottomSheetPortalContext = createContext(null);

const BottomSheetPortal = (props) => {
  const {aboveOtherModals = true} = props;
  return createPortal(
    <BottomSheetPortalContext.Provider value={props}>
      <div
        className={cx('bottom-sheet__portal', {
          'bottom-sheet__portal__above-others': aboveOtherModals,
        })}
      >
        {props.children}
      </div>
    </BottomSheetPortalContext.Provider>,
    document.body
  );
};

const Fade = forwardRef(({children}, ref) => {
  const {in: IN} = useContext(BottomSheetPortalContext);

  return (
    <CSSTransition in={IN} timeout={300} nodeRef={ref} classNames="fade" unmountOnExit>
      {children}
    </CSSTransition>
  );
});

const SlideUp = forwardRef(({children}, ref) => {
  const {in: IN} = useContext(BottomSheetPortalContext);

  return (
    <CSSTransition in={IN} timeout={300} nodeRef={ref} classNames="slide-up" appear unmountOnExit>
      {children}
    </CSSTransition>
  );
});

const Sheet = forwardRef(
  (
    {
      content,
      title,
      onClose,
      className,
      'data-qa-id': dataQaId,
      tall = false,
      hideHeaderDivider = true,
    },
    ref
  ) => (
    <div className={cx('bottom-sheet', {tall}, className)} ref={ref}>
      {title && (
        <div className="bottom-sheet__header">
          <Typography variant="header5">{title}</Typography>
          <Button
            variant="unstyled-button"
            aria-label="Close"
            onClick={onClose}
            data-qa-id={`${dataQaId}__header_close_button`}
          >
            <CloseLineIcon ratio={0.75} />
          </Button>
        </div>
      )}
      {!hideHeaderDivider && <Divider />}
      {content ?? null}
    </div>
  )
);
